<section class="pickup-drop-off" aria-labelledby="pickup-drop-off-heading">
  <div class="pickup-drop-off__header">
    <puro-typography id="pickup-drop-off-heading" variant="h5" component="h2" color="primary">
      {{ 'createShipment.serviceStep.pickupDropOff.title' | translate }}
    </puro-typography>

    <fieldset
      class="pickup-drop-off__method-toggle"
      [attr.aria-label]="'createShipment.serviceStep.pickupDropOff.methodGroupLabel' | translate"
    >
      <legend class="pickup-drop-off__method-legend">
        {{ 'createShipment.serviceStep.pickupDropOff.methodGroupLabel' | translate }}
      </legend>

      <label
        class="pickup-drop-off__method-option"
        [class.pickup-drop-off__method-option--selected]="state().method === 'drop-off'"
      >
        <input
          class="pickup-drop-off__method-input"
          type="radio"
          name="inductionMethod"
          value="drop-off"
          [checked]="state().method === 'drop-off'"
          (change)="onMethodChange('drop-off')"
        />
        <span class="pickup-drop-off__method-label">{{
          'createShipment.serviceStep.pickupDropOff.dropOffLabel' | translate
        }}</span>
      </label>

      <label
        class="pickup-drop-off__method-option"
        [class.pickup-drop-off__method-option--selected]="state().method === 'pickup'"
        [class.pickup-drop-off__method-option--disabled]="pickupMethodDisabled()"
      >
        <input
          class="pickup-drop-off__method-input"
          type="radio"
          name="inductionMethod"
          value="pickup"
          [checked]="state().method === 'pickup'"
          [disabled]="pickupMethodDisabled()"
          (change)="onMethodChange('pickup')"
        />
        <span class="pickup-drop-off__method-label">{{
          'createShipment.serviceStep.pickupDropOff.pickupLabel' | translate
        }}</span>
      </label>

      <label
        class="pickup-drop-off__method-option"
        [class.pickup-drop-off__method-option--selected]="state().method === 'label-only'"
      >
        <input
          class="pickup-drop-off__method-input"
          type="radio"
          name="inductionMethod"
          value="label-only"
          [checked]="state().method === 'label-only'"
          (change)="onMethodChange('label-only')"
        />
        <span class="pickup-drop-off__method-label">{{
          'createShipment.serviceStep.pickupDropOff.labelOnlyLabel' | translate
        }}</span>
      </label>
    </fieldset>

    <div class="pickup-drop-off__method-select" data-testid="method-select">
      <app-floating-select
        [label]="'createShipment.serviceStep.pickupDropOff.mobileMethodLabel' | translate"
        [options]="methodOptions()"
        [fullWidth]="true"
        [ngModel]="state().method"
        (ngModelChange)="onMethodChange($event)"
      />
    </div>
  </div>

  @if (state().method === 'drop-off') {
    <puro-typography variant="body1" component="p" class="pickup-drop-off__intro">
      {{ 'createShipment.serviceStep.pickupDropOff.dropOffIntroBefore' | translate }}
      <span class="pickup-drop-off__intro-emphasis">{{
        'createShipment.serviceStep.pickupDropOff.dropOffIntroEmphasis' | translate
      }}</span>
      {{ 'createShipment.serviceStep.pickupDropOff.dropOffIntroAfter' | translate }}
    </puro-typography>

    <div
      class="pickup-drop-off__panel pickup-drop-off__panel--drop-off"
      data-testid="drop-off-panel"
    >
      @if (shipFromPostal()) {
        <app-nearest-drop-off-location
          [postalCode]="shipFromPostal()!"
          [location]="state().nearestDropOff"
          [loading]="locatorLoading()"
          [errorMessage]="locatorError()"
        />
      } @else {
        <puro-typography variant="body2" class="pickup-drop-off__slot-placeholder">
          {{ 'createShipment.serviceStep.pickupDropOff.dropOffPlaceholder' | translate }}
        </puro-typography>
      }
    </div>
  } @else if (state().method === 'label-only') {
    <puro-typography
      variant="body1"
      component="p"
      class="pickup-drop-off__intro"
      data-testid="label-only-panel"
    >
      {{ 'createShipment.serviceStep.pickupDropOff.labelOnlyIntro' | translate }}
    </puro-typography>
  } @else {
    <puro-typography variant="body1" component="p" class="pickup-drop-off__intro">
      {{ 'createShipment.serviceStep.pickupDropOff.pickupIntro' | translate }}
    </puro-typography>

    <div class="pickup-drop-off__panel pickup-drop-off__panel--pickup" data-testid="pickup-panel">
      @if (pickupNotSupportedAtAddress()) {
        <app-alert-toast
          severity="error"
          live="assertive"
          data-testid="pickup-unavailable-notice"
          class="pickup-drop-off__unavailable-notice"
        >
          {{
            'createShipment.serviceStep.pickupDropOff.availability.notSupportedAtAddressBefore'
              | translate
          }}
          <button
            type="button"
            class="pickup-drop-off__unavailable-link"
            data-testid="pickup-unavailable-drop-off-link"
            (click)="onMethodChange('drop-off')"
          >
            {{
              'createShipment.serviceStep.pickupDropOff.availability.notSupportedAtAddressLink'
                | translate
            }}
          </button>
          {{
            'createShipment.serviceStep.pickupDropOff.availability.notSupportedAtAddressAfter'
              | translate
          }}
        </app-alert-toast>
      } @else {
        <app-pickup-dates-section
          [dates]="dateOptions()"
          [selectedDate]="state().selectedDate"
          [loading]="availabilityLoading()"
          [errorMessage]="availabilityError()"
          [noticeMessage]="availabilityNotice()"
          (dateChange)="dateChange.emit($event)"
        />
      }

      @if (
        !pickupNotSupportedAtAddress() &&
        !availabilityLoading() &&
        !availabilityError() &&
        dateOptions().length > 0
      ) {
        <app-pickup-window-section
          [fromOptions]="windowOptions().fromOptions"
          [toOptions]="windowOptions().toOptions"
          [windowFrom]="state().windowFrom"
          [windowTo]="state().windowTo"
          [windowNotice]="windowNotice()"
          (windowChange)="windowChange.emit($event)"
        />
      }

      <app-pickup-address-contact-section
        [address]="addressDisplay()"
        [locationOptions]="locationOptions()"
        [locationCode]="locationSelectValue()"
        [courierInstructions]="state().courierInstructions"
        [locationsLoading]="locationsLoading()"
        [locationsError]="locationsError()"
        [showSchedulingFields]="!pickupNotSupportedAtAddress()"
        (locationCodeChange)="onLocationSelectChange($event)"
        (courierInstructionsChange)="courierInstructionsChange.emit($event)"
      />
    </div>
  }
</section>
